import React, { useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components/macro';
import { NavLink } from 'react-router-dom';
import { LoadingIndicator } from 'lib/components';
import { addThousandCommaSeparator } from 'lib/utils/functions';
import { NotFound } from 'app/pages/notFound/NotFound';
import {
  TopCtaVideosResponseData,
  useGetTopCtaVideosQuery,
} from 'lib/api/metrics/queryTopCta';
import {
  MostViewedVideosResponseData,
  useGetMostViewedVideosQuery,
} from 'lib/api/metrics/queryTopVideos';

const DATE_FORMAT = 'YYYY-MM-DD';

const Sidebar = styled.div`
  border-radius: 4px;
  box-shadow: 0 4px 12px 0 #1d1e240a;
  border: solid 1px #f7f8fa;
  padding: 24px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: #272a32;
`;

const Content = styled.div`
  margin-top: 16px;
`;

const ItemContainer = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

const ItemNumber = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #9297a2;
`;

type ItemTitleProps = {
  clickable: boolean;
};

const ItemTitle = styled.div<ItemTitleProps>`
  margin: 0 16px;

  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  text-decoration: underline;
  cursor: ${props => (props.clickable ? 'pointer' : 'auto')};
  text-decoration: ${props => (props.clickable ? 'underline' : 'none')};
  color: ${props => (props.clickable ? '#001b53' : '#272a32')};
`;

const ItemCount = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  text-align: right;
  color: #272a32;
`;

type Props = {
  title: string;
  path: 'ctas' | 'videos';
  from: Date;
  to: Date;
  users?: string[];
};

export const SidebarList = (props: Props) => {
  const { title, path, from, to, users = [] } = props;
  const [retryCount, setRetryCount] = useState(0);

  const params = {
    from: dayjs(from).format(DATE_FORMAT),
    to: dayjs(to).format(DATE_FORMAT),
    users: users.join(','),
    retryCount,
  };

  const {
    data: ctas,
    error: ctasError,
    isLoading: isLoadingCtas,
  } = useGetTopCtaVideosQuery({ params, enabled: path === 'ctas' });
  const {
    data: videos,
    error: videosError,
    isLoading: isLoadingVideos,
  } = useGetMostViewedVideosQuery({ params, enabled: path === 'videos' });

  const isVideos = path === 'videos';

  const data = isVideos ? videos : ctas;

  const showLoading = isLoadingCtas || isLoadingVideos;
  const error = ctasError || videosError;

  if (error) {
    return <NotFound />;
  }

  return (
    <Sidebar>
      <Title>{title}</Title>
      <Content>
        {showLoading && (
          <LoadingIndicator
            isLoading={showLoading}
            height='250px'
            error={error}
            onRetry={() => setRetryCount(retryCount + 1)}
          />
        )}
        {!showLoading && (
          <>
            {!data?.length && <div>No data to show.</div>}
            {isVideos &&
              videos &&
              videos.length > 0 &&
              videos.map(
                (item: MostViewedVideosResponseData, index: number) => {
                  const clickable = !!item.videoId;
                  const content = (
                    <ItemContainer key={index}>
                      <ItemNumber>{index + 1}</ItemNumber>
                      <ItemTitle clickable={clickable}>{item.title}</ItemTitle>
                      <ItemCount>
                        {addThousandCommaSeparator(parseInt(item.cnt, 10))}
                      </ItemCount>
                    </ItemContainer>
                  );
                  if (!clickable) {
                    return content;
                  }
                  return (
                    <NavLink
                      key={index}
                      to={
                        item.videoId
                          ? `/classic/edit-video/${item.videoId}/details`
                          : '#'
                      }
                      style={{ textDecoration: 'none' }}
                    >
                      {content}
                    </NavLink>
                  );
                }
              )}
            {!isVideos &&
              ctas &&
              ctas.length > 0 &&
              ctas.map((item: TopCtaVideosResponseData, index: number) => {
                const content = (
                  <ItemContainer key={index}>
                    <ItemNumber>{index + 1}</ItemNumber>
                    <ItemTitle clickable={false}>{item.linkText}</ItemTitle>
                    <ItemCount>
                      {addThousandCommaSeparator(parseInt(item.cnt, 10))}
                    </ItemCount>
                  </ItemContainer>
                );
                return content;
              })}
          </>
        )}
      </Content>
    </Sidebar>
  );
};
